/**
 * Phase 15.4.1 — Share Card Control (Admin)
 * -----------------------------------------
 * Founder/admin tools to edit every text label, theme, and visibility
 * toggle that goes onto an artist's downloadable share card — without
 * needing a code change.
 *
 * Layout:
 *   [ tabs: Story | Square | Header ]
 *   [ left: editable form ]  [ right: live preview ]
 *   [ bottom: audit log ]
 *
 * Live preview renders the same `NowFeaturedCard` component the modal
 * uses — guaranteed preview/export parity. The card is scaled-down to
 * fit the viewport.
 *
 * Save flow:
 *   1. Edit fields → local state diverges from `serverConfig`
 *   2. "Save" → PUT /api/admin/share-cards/config/{format}
 *   3. Audit row appears in the log
 *
 * Reset flow:
 *   "Reset to default" → POST /api/admin/share-cards/reset/{format}
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import NowFeaturedCard from "@/components/NowFeaturedCard";
import { useRequireAdmin } from "@/lib/useRequireAdmin";

const FORMATS = [
  { id: "story",  label: "Story",  size: "1080 × 1920", w: 1080, h: 1920 },
  { id: "square", label: "Square", size: "1080 × 1080", w: 1080, h: 1080 },
  { id: "wide",   label: "Header", size: "1600 ×  900", w: 1600, h: 900 },
];

// Sample data used to render the preview card. Real share modals pass
// the live submission, but for the admin preview a generic dataset is
// enough to see how text/themes/toggles look.
const SAMPLE_DATA = {
  track_title: "Sample Track Title",
  artist_handle: "yourartist",
  display_plays: 124857,
  reach: 42390,
  impressions: 198421,
  badges: ["spotlight_winner", "verified_artist"],
  share_url: "rgmstudio.org/track/sample",
  cover: "https://res.cloudinary.com/demo/image/upload/sample.jpg",
  avatar: "https://res.cloudinary.com/demo/image/upload/sample.jpg",
};

const FIELD_LABELS = {
  title_text:     { label: "Title Ribbon",     hint: "Top header pill text. Keep under 40 chars." },
  tagline_text:   { label: "Tagline",          hint: "Small accent text above the track title." },
  cta_text:       { label: "CTA Pill",         hint: "Top-right callout on the cover image." },
  watermark_text: { label: "Watermark",        hint: "Bottom branding line (story format)." },
  footer_text:    { label: "Footer",           hint: "Bottom URL / call-to-action line." },
  fallback_image_url: { label: "Fallback image URL", hint: "Used when cover/avatar can't be embedded. https:// only." },
};

const TOGGLE_LABELS = {
  show_cover:     "Show cover art",
  show_avatar:    "Show profile photo",
  show_stats:     "Show stats badges",
  show_badges:    "Show artist badges",
  show_watermark: "Show watermark",
};

const AdminShareCardControlPage = () => {
  const { authChecked } = useRequireAdmin();
  const [serverConfigs, setServerConfigs] = useState(null);
  const [themes, setThemes] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [audit, setAudit] = useState([]);
  const [activeFormat, setActiveFormat] = useState("story");
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  // Feb 2026 — Share Visibility (CEO ON/OFF per content type). Reuses
  // the existing platform_config architecture — no new admin page,
  // no new route, no new nav entry. Extends the SAME admin surface.
  const SHARE_VIS_KEYS = [
    { key: "tracks",     label: "Tracks",              hint: "In-app Share buttons + public OG links (/api/share/track, /submission, /og/track, /milestone)" },
    { key: "playlists",  label: "Playlists",           hint: "In-app Share button + public OG link (/api/share/playlist)" },
    { key: "contests",   label: "Contests",            hint: "In-app Share button + public OG link (/api/share/contest)" },
    { key: "profiles",   label: "Profiles",            hint: "In-app Profile Share menu only (no backend OG route)" },
    { key: "beats",      label: "Beats",               hint: "In-app Beat Share to Community + Copy link only (no backend OG route)" },
    { key: "reports",    label: "Reports",             hint: "No Share action currently — reserved" },
    { key: "spotlight",  label: "Spotlight",           hint: "No Share action currently — reserved" },
    { key: "winners",    label: "Winners / Champions", hint: "In-app Winner Share Card modal only (no backend OG route)" },
  ];
  const SHARE_VIS_DEFAULTS = SHARE_VIS_KEYS.reduce((a, x) => (a[x.key] = true, a), {});
  const [shareVisibility, setShareVisibility] = useState(SHARE_VIS_DEFAULTS);
  const [shareVisSaving, setShareVisSaving] = useState(false);

  const loadShareVisibility = useCallback(async () => {
    try {
      const r = await api.get("/admin/share-visibility");
      const v = r?.data?.value || {};
      setShareVisibility({ ...SHARE_VIS_DEFAULTS, ...v });
    } catch { /* keep defaults — safe fallback */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleShareVisibility = async (k, v) => {
    setShareVisSaving(true);
    const next = { ...shareVisibility, [k]: !!v };
    setShareVisibility(next);
    try {
      await api.put("/admin/share-visibility", next);
      toast.success(`Share for ${k} ${v ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      // Revert on failure.
      setShareVisibility(shareVisibility);
    } finally {
      setShareVisSaving(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/share-cards/config");
      setServerConfigs(r.data?.configs || {});
      setThemes(r.data?.themes || {});
      setDefaults(r.data?.defaults || {});
      setAudit(r.data?.audit_head || []);
      // Hydrate drafts only on first load; preserve any unsaved edits.
      setDrafts((prev) => {
        if (Object.keys(prev).length > 0) return prev;
        return { ...(r.data?.configs || {}) };
      });
      setLoadErr(null);
    } catch (e) {
      setLoadErr(e?.response?.data?.detail || e?.message || "Failed to load config");
    }
  }, []);

  useEffect(() => { if (authChecked) { load(); loadShareVisibility(); } }, [authChecked, load, loadShareVisibility]);

  const activeDraft = useMemo(() => drafts[activeFormat] || {}, [drafts, activeFormat]);
  const activeServer = useMemo(() => serverConfigs?.[activeFormat] || {}, [serverConfigs, activeFormat]);
  const isDirty = useMemo(() => {
    const keys = [
      "title_text", "tagline_text", "cta_text", "watermark_text", "footer_text",
      "theme", "show_cover", "show_avatar", "show_stats", "show_badges",
      "show_watermark", "fallback_image_url",
    ];
    return keys.some((k) => activeDraft[k] !== activeServer[k]);
  }, [activeDraft, activeServer]);

  const updateDraft = (patch) => {
    setDrafts((prev) => ({
      ...prev,
      [activeFormat]: { ...(prev[activeFormat] || {}), ...patch },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build the minimal patch (only changed fields) so audit rows are tight.
      const patch = {};
      for (const k of Object.keys(activeDraft)) {
        if (activeDraft[k] !== activeServer[k]) patch[k] = activeDraft[k];
      }
      if (Object.keys(patch).length === 0) {
        toast.info("No changes to save");
        return;
      }
      const r = await api.put(`/admin/share-cards/config/${activeFormat}`, patch);
      const newCfg = r.data?.config;
      setServerConfigs((prev) => ({ ...(prev || {}), [activeFormat]: newCfg }));
      setDrafts((prev) => ({ ...prev, [activeFormat]: newCfg }));
      toast.success(`Saved ${activeFormat} config`);
      // Refresh audit head so the new entry shows up.
      const a = await api.get("/admin/share-cards/audit");
      setAudit(a.data?.entries || []);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.msg || "Save failed");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(`Reset ${activeFormat} to defaults? This can't be undone (but the change is logged).`)) return;
    setResetting(true);
    try {
      const r = await api.post(`/admin/share-cards/reset/${activeFormat}`);
      const cfg = r.data?.config;
      setServerConfigs((prev) => ({ ...(prev || {}), [activeFormat]: cfg }));
      setDrafts((prev) => ({ ...prev, [activeFormat]: cfg }));
      toast.success(`${activeFormat} reset to defaults`);
      const a = await api.get("/admin/share-cards/audit");
      setAudit(a.data?.entries || []);
    } catch (e) {
      toast.error("Reset failed");
    } finally {
      setResetting(false);
    }
  };

  // Preview scaling — fit a target width into ~480px viewport.
  const activeMeta = FORMATS.find((f) => f.id === activeFormat);
  const previewMaxW = 480;
  const previewScale = activeMeta ? Math.min(1, previewMaxW / activeMeta.w) : 0.4;

  if (loadErr && !serverConfigs) {
    return (
      <div className="min-h-screen bg-[#0a0a0e] p-6 text-white">
        <h1 className="text-2xl font-black mb-3">Share Card Control</h1>
        <Card className="p-4 border-rose-500/40 bg-rose-500/5">
          <div className="text-rose-200 font-bold">Failed to load</div>
          <div className="text-rose-100/80 text-sm mt-1" data-testid="scc-load-error">{loadErr}</div>
          <Button onClick={load} className="mt-3 bg-amber-500 hover:bg-amber-400 text-black font-bold" data-testid="scc-retry">
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0e] p-4 sm:p-6 text-white" data-testid="share-card-control-page">
      <div className="mb-5">
        <h1 className="text-2xl sm:text-3xl font-black" data-testid="scc-title">🎨 Share Card Control</h1>
        <p className="text-sm text-gray-400 mt-1">
          Edit every text label, theme, and toggle on artist share cards — no code changes needed.
        </p>
      </div>

      {/* Format tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {FORMATS.map((f) => (
          <button
            key={f.id}
            onClick={() => setActiveFormat(f.id)}
            data-testid={`scc-tab-${f.id}`}
            className={
              "px-4 py-2 rounded-lg font-bold text-sm transition " +
              (activeFormat === f.id
                ? "bg-gradient-to-r from-pink-600 to-purple-600 text-white shadow-lg"
                : "bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10")
            }
          >
            {f.label} <span className="opacity-60 text-[10px] ml-1">{f.size}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_540px] gap-6">
        {/* LEFT: form */}
        <Card className="p-5 bg-white/[0.02] border-white/10" data-testid="scc-form-card">
          {/* Theme picker */}
          <h2 className="text-base font-black mb-3">Theme</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
            {themes && Object.values(themes).map((t) => (
              <button
                key={t.id}
                onClick={() => updateDraft({ theme: t.id })}
                data-testid={`scc-theme-${t.id}`}
                className={
                  "p-2 rounded-lg border text-left transition " +
                  (activeDraft.theme === t.id
                    ? "border-pink-400 bg-pink-500/10"
                    : "border-white/10 bg-white/[0.02] hover:bg-white/5")
                }
              >
                <div
                  className="h-12 rounded mb-1"
                  style={{ background: `linear-gradient(135deg, ${t.palette.bg_a}, ${t.palette.bg_b}), ${t.palette.accent_ribbon}`, backgroundBlendMode: "overlay" }}
                />
                <div className="text-sm text-white font-bold">{t.name}</div>
                <div className="text-[10px] text-gray-400 truncate">{t.tagline}</div>
              </button>
            ))}
          </div>

          {/* Text fields */}
          <h2 className="text-base font-black mb-3">Text</h2>
          <div className="space-y-3 mb-5">
            {["title_text", "tagline_text", "cta_text", "watermark_text", "footer_text"].map((f) => (
              <div key={f}>
                <label className="text-[11px] uppercase tracking-wide text-gray-400 font-bold">
                  {FIELD_LABELS[f].label}
                </label>
                <Input
                  type="text"
                  value={activeDraft[f] ?? ""}
                  onChange={(e) => updateDraft({ [f]: e.target.value })}
                  className="mt-1 bg-white/5 border-white/15 text-white"
                  maxLength={200}
                  data-testid={`scc-input-${f}`}
                />
                <p className="text-[10px] text-gray-500 mt-1">{FIELD_LABELS[f].hint}</p>
              </div>
            ))}
            <div>
              <label className="text-[11px] uppercase tracking-wide text-gray-400 font-bold">
                {FIELD_LABELS.fallback_image_url.label}
              </label>
              <Input
                type="text"
                value={activeDraft.fallback_image_url ?? ""}
                onChange={(e) => updateDraft({ fallback_image_url: e.target.value || null })}
                placeholder="https://..."
                className="mt-1 bg-white/5 border-white/15 text-white"
                data-testid="scc-input-fallback_image_url"
              />
              <p className="text-[10px] text-gray-500 mt-1">{FIELD_LABELS.fallback_image_url.hint}</p>
            </div>
          </div>

          {/* Toggles */}
          <h2 className="text-base font-black mb-3">Visibility</h2>
          <div className="space-y-2 mb-5">
            {Object.entries(TOGGLE_LABELS).map(([k, label]) => (
              <label
                key={k}
                className="flex items-center justify-between rounded-md px-3 py-2 bg-white/[0.02] border border-white/10"
                data-testid={`scc-toggle-row-${k}`}
              >
                <span className="text-sm text-white">{label}</span>
                <Switch
                  checked={!!activeDraft[k]}
                  onCheckedChange={(v) => updateDraft({ [k]: v })}
                  data-testid={`scc-toggle-${k}`}
                />
              </label>
            ))}
          </div>

          {/* Feb 2026 — Share Visibility (per content type). Turns the
              user-facing Share action ON/OFF platform-wide. Never
              deletes or hides the underlying content. Extends the
              existing platform_config architecture. */}
          <h2 className="text-base font-black mb-3">Share Visibility (per content type)</h2>
          <p className="text-[11px] text-gray-500 mb-2">
            Turning a switch OFF hides the corresponding Share action across the platform.
            It does NOT delete or hide the underlying content pages.
          </p>
          <div className="space-y-2 mb-5" data-testid="scc-share-visibility-list">
            {SHARE_VIS_KEYS.map(({ key, label, hint }) => (
              <label
                key={key}
                className="flex items-start justify-between gap-3 rounded-md px-3 py-2 bg-white/[0.02] border border-white/10"
                data-testid={`scc-share-vis-row-${key}`}
              >
                <div className="min-w-0">
                  <div className="text-sm text-white">{label}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5" data-testid={`scc-share-vis-hint-${key}`}>{hint}</div>
                </div>
                <Switch
                  checked={shareVisibility[key] !== false}
                  disabled={shareVisSaving}
                  onCheckedChange={(v) => toggleShareVisibility(key, v)}
                  data-testid={`scc-share-vis-${key}`}
                />
              </label>
            ))}
          </div>

          {/* Action bar */}
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="bg-gradient-to-r from-pink-600 to-purple-600 text-white font-bold"
              data-testid="scc-save"
            >
              {saving ? "Saving…" : isDirty ? "Save changes" : "Saved"}
            </Button>
            <Button
              onClick={handleReset}
              disabled={resetting}
              variant="outline"
              className="border-rose-400/40 text-rose-200 hover:bg-rose-500/10"
              data-testid="scc-reset"
            >
              {resetting ? "Resetting…" : "Reset to default"}
            </Button>
            {isDirty && (
              <span className="text-[11px] text-amber-200/80 self-center ml-1" data-testid="scc-dirty-indicator">
                Unsaved changes
              </span>
            )}
          </div>
        </Card>

        {/* RIGHT: live preview */}
        <Card className="p-4 bg-white/[0.02] border-white/10 self-start sticky top-4" data-testid="scc-preview-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-black">Live Preview</h2>
            <span className="text-[10px] text-gray-500">{activeMeta?.size}</span>
          </div>
          {activeMeta && (
            <div
              className="relative bg-black/40 rounded-lg overflow-hidden mx-auto"
              style={{ width: activeMeta.w * previewScale, height: activeMeta.h * previewScale }}
            >
              <div style={{ transform: `scale(${previewScale})`, transformOrigin: "top left" }}>
                <NowFeaturedCard
                  layout={activeFormat}
                  data={SAMPLE_DATA}
                  config={activeDraft}
                />
              </div>
            </div>
          )}
          <p className="text-[10px] text-gray-500 mt-2 text-center">
            Preview uses sample data. Real exports use the live track + artist info.
          </p>
        </Card>
      </div>

      {/* Audit log */}
      <Card className="p-5 bg-white/[0.02] border-white/10 mt-6" data-testid="scc-audit-card">
        <h2 className="text-base font-black mb-3">📝 Audit log</h2>
        {audit.length === 0 ? (
          <p className="text-sm text-gray-400">No edits yet.</p>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {audit.map((e, i) => (
              <div
                key={e._id || i}
                data-testid={`scc-audit-${i}`}
                className="rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-xs"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={
                    "uppercase tracking-wide font-black px-2 py-0.5 rounded-full text-[10px] border " +
                    (e.action === "reset"
                      ? "border-rose-400/40 bg-rose-500/10 text-rose-200"
                      : "border-emerald-400/40 bg-emerald-500/10 text-emerald-200")
                  }>
                    {e.action}
                  </span>
                  <span className="font-bold text-white">{e.format}</span>
                  <span className="text-gray-400">by {e.admin_email || e.admin_id || "unknown"}</span>
                  <span className="text-gray-500 ml-auto text-[10px]">{e.at}</span>
                </div>
                {e.changes && Object.entries(e.changes).map(([field, diff]) => (
                  <div key={field} className="ml-1 mt-1 text-[11px] text-gray-300 font-mono">
                    <span className="text-pink-300">{field}</span>:&nbsp;
                    <span className="text-gray-500 line-through">{String(diff.old).slice(0, 40)}</span>
                    &nbsp;→&nbsp;
                    <span className="text-emerald-300">{String(diff.new).slice(0, 40)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminShareCardControlPage;
